// ABOUTME: Custom hook for drag and drop functionality
// ABOUTME: Handles drag state, events, and file extraction with proper cleanup

import { useState, useCallback, useRef, useEffect } from 'react';
import { FILE_UPLOAD_CONSTANTS } from '../utils/constants';

interface UseDragDropOptions {
  isDisabled?: boolean;
  onDrop?: (files: File[]) => void;
  validateOnDrop?: (files: File[]) => boolean;
  acceptedFileTypes?: string[];
}

export const useDragDrop = (options: UseDragDropOptions = {}) => {
  const { isDisabled = false, onDrop, validateOnDrop, acceptedFileTypes } = options;

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const dragTimeoutRef = useRef<NodeJS.Timeout>();

  // Reset drag state with optimized delay to prevent flashing while maintaining smooth UX
  const resetDragState = useCallback(() => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }

    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(false);
      dragCounter.current = 0;
    }, FILE_UPLOAD_CONSTANTS.ANIMATION.DRAG_OVERLAY_FADE);
  }, []);

  // Check if drag event contains files
  const hasFiles = useCallback((event: DragEvent): boolean => {
    if (!event.dataTransfer) return false;

    // Check if there are files in the data transfer
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      return true;
    }

    // Check the types array for 'Files' type
    if (event.dataTransfer.types) {
      for (let i = 0; i < event.dataTransfer.types.length; i++) {
        if (event.dataTransfer.types[i] === 'Files') {
          return true;
        }
      }
    }

    return false;
  }, []);

  // Extract files from drag event
  const extractFiles = useCallback(
    (event: DragEvent): File[] => {
      if (!event.dataTransfer?.files) return [];

      const files: File[] = [];
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        const file = event.dataTransfer.files[i];

        // Filter by accepted types if specified
        if (acceptedFileTypes) {
          const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
          if (acceptedFileTypes.includes(extension)) {
            files.push(file);
          }
        } else {
          files.push(file);
        }
      }

      return files;
    },
    [acceptedFileTypes],
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (isDisabled || !hasFiles(event.nativeEvent)) return;

      dragCounter.current++;

      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      if (dragCounter.current === 1) {
        setIsDragging(true);
      }
    },
    [isDisabled, hasFiles],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (isDisabled) return;

      dragCounter.current--;

      if (dragCounter.current === 0) {
        resetDragState();
      }
    },
    [isDisabled, resetDragState],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (isDisabled || !hasFiles(event.nativeEvent)) return;

      // Set the drop effect
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [isDisabled, hasFiles],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      dragCounter.current = 0;
      setIsDragging(false);

      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      if (isDisabled) return;

      const files = extractFiles(event.nativeEvent);

      if (files.length === 0) return;

      // Validate files if validator provided
      if (validateOnDrop && !validateOnDrop(files)) {
        return;
      }

      // Call onDrop callback
      if (onDrop) {
        onDrop(files);
      }
    },
    [isDisabled, extractFiles, validateOnDrop, onDrop],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Get props to spread on the drop zone element with enhanced accessibility
  const getDropZoneProps = useCallback(
    () => ({
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      'aria-label': isDragging
        ? FILE_UPLOAD_CONSTANTS.ARIA_LABELS.DROPZONE_ACTIVE
        : FILE_UPLOAD_CONSTANTS.ARIA_LABELS.DROPZONE,
      'aria-dropeffect': (isDisabled ? 'none' : 'copy') as 'none' | 'copy',
      'data-dragging': isDragging,
      role: 'region',
    }),
    [handleDragEnter, handleDragLeave, handleDragOver, handleDrop, isDisabled, isDragging],
  );

  return {
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    getDropZoneProps,
    resetDragState,
  };
};
